/**
 * Fat hook for the client's Polly conversations tab (insurescope-m6vie;
 * pattern parity with `useClientListTable` / `useDocumentListTable`).
 * Owns the paged query (keepPreviousData across q/page/size swaps WITHIN
 * a client; a clientId swap renders as loading instead — lb2k2), the
 * debounced search, the pagination state machine, and the branch
 * discriminator; the component is a thin renderer.
 *
 * Unit 6 (insurescope-7149k.2.3.5): numbered pagination replaces the v1
 * infinite query. Controlled page/pageSize are LOCAL state, not URL
 * (`useClientListTable` precedent: the page number is ephemeral browsing
 * state, unlike `?folder=` which is a place). Contract: the 6.DESIGN
 * brief on insurescope-7149k.2.3.1 +
 * docs/design-briefs/2026-06-03-client-conversations.md.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDebouncedEffect } from "@react-hookz/web";
import { useEffect, useState } from "react";

import { getConversations, type IChatConversationListItem } from "@/api";

export type ClientConversationsBranch =
    | "loading"
    | "error"
    | "empty-search"
    | "empty-library"
    | "populated";

/**
 * The smallest PaginationToolbar size option anchors BOTH constants:
 * chrome appears only when there is something to page at the smallest
 * size, and the default size matches it so threshold and default agree
 * (structural coupling per the 2.3.8 review — don't split them).
 */
const SMALLEST_SIZE_OPTION = 10;
const PAGINATION_THRESHOLD = SMALLEST_SIZE_OPTION;
const DEFAULT_PAGE_SIZE = SMALLEST_SIZE_OPTION;

/**
 * Query-key prefix for the tab's conversations list. ClientDetail
 * invalidates `[CLIENT_CONVERSATIONS_KEY, clientId]` when the Ask Polly
 * sheet closes (prefix match, so every q/page/size variant goes stale
 * together), so a sheet-created conversation appears without a manual
 * refresh. Consumers import it from the TAB module (the public seam); it
 * lives here because the key belongs with the query it addresses.
 */
export const CLIENT_CONVERSATIONS_KEY = "client-conversations";

export interface UseClientConversationsTabResult {
    branch: ClientConversationsBranch;
    conversations: IChatConversationListItem[];
    /** The live input value (undebounced; typing stays instant). */
    search: string;
    setSearch: (value: string) => void;
    /**
     * Trimmed + debounced: what the requests actually carry. Branch and
     * empty-search copy derive from THIS value, never from `search`, so
     * headline and branch always agree (the sibling hooks' rule).
     */
    q: string;
    /** True while keepPreviousData shows the OLD rows during a key swap. */
    isPlaceholderData: boolean;
    refetch: () => void;
    /** `total > 10`: the tab mounts PaginationToolbar only when true. */
    showToolbar: boolean;
    total: number;
    /** 1-based, optimistic (reflects the clicked target immediately). */
    page: number;
    pages: number;
    pageSize: number;
    canPreviousPage: boolean;
    canNextPage: boolean;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
}

export function useClientConversationsTab(
    clientId: number
): UseClientConversationsTabResult {
    const [search, setSearch] = useState("");
    // DocumentList precedent: debounce the QUERY, not the input, so typing
    // stays instant while requests wait for a 300 ms pause. Each settled q
    // is one request (for page 1, below); no extra debounce work needed
    // post-rewrite — recorded on the bead.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useDebouncedEffect(() => setDebouncedSearch(search), [search], 300);

    const q = debouncedSearch.trim();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    // A new q is a new result set; a new client is a new dataset. Either
    // way page 2 of the old context is meaningless — back to page 1.
    // Render-time adjustment (not an effect): the reset must land BEFORE
    // the query reads its key, or the first request of a fresh search
    // asks for the old page number (caught by RED.B's q-reset scenario).
    // Deliberately STRONGER than useClientListTable's effect-based reset;
    // it matches DocumentList's resultSetKey/effectivePagination approach.
    const [prevContext, setPrevContext] = useState<[number, string]>([
        clientId,
        q,
    ]);
    if (prevContext[0] !== clientId || prevContext[1] !== q) {
        setPrevContext([clientId, q]);
        setPage(1);
    }

    const { data, isPending, isPlaceholderData, isError, refetch } = useQuery({
        queryKey: [CLIENT_CONVERSATIONS_KEY, clientId, q, page, pageSize],
        queryFn: () =>
            // include_shared: the client hub surfaces conversations shared WITH
            // the requester alongside owned ones (matches the shared-inclusive
            // client counts).
            getConversations(
                page,
                pageSize,
                q ? { q } : undefined,
                clientId,
                true
            ),
        // Holds the old rows on screen during any key swap so the list
        // (and input focus) never unmounts mid-transition; the component
        // dims them via isPlaceholderData.
        placeholderData: keepPreviousData,
    });

    // Which client the CURRENT `data` belongs to (insurescope-lb2k2):
    // keepPreviousData across a CLIENT swap would dress client A's rows
    // (dimmed) in client B's workspace — on a per-user/per-client surface
    // that reads as a leak in a screenshot. Track the last-resolved
    // client; a placeholder borrowed from a DIFFERENT client renders as
    // loading instead. q/page/size swaps within a client keep the
    // dimmed-previous-rows treatment (deliberate v1 design, suite-pinned).
    // Render-time adjustment, same idempotent pattern as prevContext.
    const [resolvedClientId, setResolvedClientId] = useState<number | null>(
        null
    );
    if (data && !isPlaceholderData && resolvedClientId !== clientId) {
        setResolvedClientId(clientId);
    }
    const crossClientPlaceholder =
        isPlaceholderData && resolvedClientId !== clientId;

    const total = data?.total ?? 0;
    const pages = data?.pages ?? 0;

    // The single clamp invariant (6.DESIGN brief §4): after every resolve,
    // `page > pages → page = max(1, pages)`. Covers a deletion emptying
    // the last page AND the shrink-below-threshold case (pages becomes 1,
    // the user lands on page 1, the toolbar unmounts) — never a stranded
    // invisible page.
    useEffect(() => {
        if (data && !isPlaceholderData && page > Math.max(1, data.pages)) {
            setPage(Math.max(1, data.pages));
        }
    }, [data, isPlaceholderData, page]);

    const conversations = data?.items ?? [];

    // Error outranks content (pinned in the suite); a cross-client
    // placeholder outranks everything below it (the old client's rows
    // must never render under the new client's workspace, lb2k2). `total`
    // (not the current slice) decides populated-ness: a clamp in flight
    // can serve an empty out-of-range slice for a frame, and that must
    // never flash the empty-library copy while conversations exist.
    const branch: ClientConversationsBranch = isPending
        ? "loading"
        : isError
          ? "error"
          : crossClientPlaceholder
            ? "loading"
            : total > 0
              ? "populated"
              : q
                ? "empty-search"
                : "empty-library";

    return {
        branch,
        conversations,
        search,
        setSearch,
        q,
        isPlaceholderData,
        refetch,
        showToolbar: total > PAGINATION_THRESHOLD,
        total,
        page,
        pages,
        pageSize,
        canPreviousPage: page > 1,
        canNextPage: page < pages,
        onPageChange: setPage,
        onPageSizeChange: (size: number) => {
            // Re-slice from the top: predictable, no offset jumps.
            setPageSize(size);
            setPage(1);
        },
    };
}
